import { isAxiosError } from 'axios';
import type { MarisaAuto } from '~/@types/MarisaAuto/form';
import type { FinishAutoInsuranceResponse } from '~/domain/Assegurou/finishAutoInsurance';
import type { FinishAutoInsuranceResponseDTO } from '~/dto/Assegurou/finishAutoInsurance';
import Env from '~/env';
import apiAssegurouAuto from '~/services/apiAssegurouAuto';
import { parseDate } from '~/utils/date';
import { registerContractUseCase } from '../PSF/RegisterContractUseCase';
import { getRawDocument, getRawZipcode } from '~/utils/string';

interface UseCaseParams {
  selectedSimulation: MarisaAuto.InsuranceQuote;
  vehicleInfo: CheckoutMarisaAuto.VehicleInfo;
  paymentInfo: CheckoutMarisaAuto.Payment;
  address: MarisaAuto.Address;
  personalInfo: CheckoutMarisaAuto.PersonalInfo;
}

const getPaymentMethodBody = (paymentInfo: CheckoutMarisaAuto.Payment) => {
  if (paymentInfo.card) {
    const [expiryDateMonth, expiryDateYear] =
      paymentInfo.card?.expiryDate.split('/') as string[];

    return {
      cartao: {
        numeroCartao: paymentInfo.card.cardNumber,
        cpfCnpj: getRawDocument(paymentInfo.card.document),
        nomeCartao: paymentInfo.card.holderName,
        mesValidade: expiryDateMonth,
        anoValidade: `20${expiryDateYear}`,
      },
    };
  }
  if (paymentInfo.debit) {
    return {
      debito: {
        cpfCnpj: getRawDocument(paymentInfo.debit?.cpf ?? ''),
        nomeTitular: paymentInfo.debit?.name,
        codBanco: paymentInfo.debit?.bank,
        agencia: paymentInfo.debit?.agency,
        conta: paymentInfo.debit?.accountNumber,
        digConta: paymentInfo.debit?.accountDigit,
      },
    };
  }
  return {};
};

export async function finishAutoInsuranceUseCase(
  data: UseCaseParams
): Promise<FinishAutoInsuranceResponse | null> {
  try {
    const {
      vehicleInfo,
      selectedSimulation,
      paymentInfo,
      address,
      personalInfo,
    } = data;

    const vehicleBody = {
      corVeiculo: vehicleInfo.color,
      renavam: vehicleInfo.renavam,
      chassi: vehicleInfo.chassi,
      placa: vehicleInfo.plate,
    };

    const addressBody = {
      cep: getRawZipcode(address.zipcode),
      cidade: address.city,
      bairro: address.neighborhood,
      logradouro: address.street,
      uf: address.state,
      numero: address.number,
      complemento: address.complement,
    };

    const body = {
      ...vehicleBody,
      numeroCotacao: selectedSimulation.numeroCotacao,
      codigoOferta: selectedSimulation.codigoOferta,
      corretora: selectedSimulation.empresaDescricao,
      seguradora: selectedSimulation.seguradora,
      dddCelular: personalInfo.phone.ddd,
      numCelular: personalInfo.phone.number,
      email: personalInfo.email,
      dtNascimento: parseDate(personalInfo.birthDate),
      sexo: personalInfo.gender,
      estadoCivil: personalInfo.maritalStatus,
      valorPremioFinal: selectedSimulation.valorPremioFinal,
      numeroCNH: personalInfo.license.number,
      dataHabilitacao: parseDate(personalInfo.license.date),
      endereco: addressBody,
      formaPagamento: {
        codigo: paymentInfo.code,
        parcelas: paymentInfo.installments,
        tipoClasse: paymentInfo.classType,
        ...getPaymentMethodBody(paymentInfo),
      },
    };

    const response =
      await apiAssegurouAuto.post<FinishAutoInsuranceResponseDTO>(
        `${Env.INSURANCE_AUTO_URL}/auto/transmissao`,
        body
      );

    if (response.data.success) {
      const transactionNumber = response.data.resultData.leadID;

      registerContractUseCase({
        transactionNumber,
        document: personalInfo.document,
        quotationNumber: selectedSimulation.numeroCotacao,
        totalPrize: Number(selectedSimulation.valorPremioFinal),
        protocol: response.data.resultData.TransmitirPropostaResult.protocolo,
        type: 'AUTO',
      });

      return {
        success: response?.data?.success,
        transactionNumber,
        errors: response?.data?.errors,
      };
    }

    throw {
      message:
        response.data.errors && response.data.errors.length > 0
          ? response.data.errors[0]?.errorMessage
          : response.data.message,
    };
  } catch (error) {
    console.log('finishAutoInsuranceUseCase error', error);
    if (isAxiosError(error) && error.response?.data?.message) {
      throw {
        message: error.response?.data?.message,
      };
    }
    throw error;
  }
}
